import CryptoJS from 'crypto-js';
import axios, { AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { makeUseAxios } from 'axios-hooks';
import { fetchAuthSession } from '@aws-amplify/auth';
import { env } from '@/config';

const httpInstance = axios.create({
  baseURL: env.API_URL,
  timeout: 30000,
});

// Add a request interceptor
httpInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig<any>) => {
    const newConfig = { ...config };

    try {
      const { tokens } = await fetchAuthSession({ forceRefresh: true });

      if (tokens?.idToken) {
        newConfig.headers = newConfig.headers || {};
        newConfig.headers['Authorization'] = `Bearer ${tokens.idToken.toString()}`;
      }
    } catch (error) {
      console.error('Error fetching auth session', error);
    }

    if (!newConfig.headers) {
      newConfig.headers = new axios.AxiosHeaders();
    }
    newConfig.headers.set('X-Version', '1.0.0');

    if (newConfig.data) {
      const demoToken = CryptoJS.HmacSHA256(JSON.stringify(newConfig.data), env.GW_API_REQ).toString();
      newConfig.headers['X-Signature'] = demoToken;
    }

    return newConfig;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor
httpInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// Custom hook using the configured axios instance
export const useAxiosInstanceMake = makeUseAxios({
  axios: httpInstance,
  defaultOptions: { manual: true },
});

interface UseAxiosResponse<T = any> {
  data: T | undefined;
  loading: boolean;
  error: AxiosError | null | undefined;
  response: AxiosResponse<T> | undefined;
}

export const useAxiosInstance = <T = any>(
  params: string | AxiosRequestConfig
): [UseAxiosResponse<T>, (config?: AxiosRequestConfig) => Promise<AxiosResponse<T>>] => {
  const [{ data, loading, error, response }, execute] = useAxiosInstanceMake(
    typeof params === 'string' ? params : { ...params }
  );

  return [{ data, loading, error, response }, execute];
};

export default httpInstance;
